import http from 'hub-http/clients/apiClient';
import { fromJS } from 'immutable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { AsyncStatus } from 'reporting-data/asyncData/AsyncStatus';
import { useAsyncFetcher } from 'reporting-data/asyncData/useAsyncFetcher';
import { logError } from 'reporting-data/monitoring/logger';
import { getReportDatasetFromReportResolve } from 'reporting-data/report/reportResolveUtils';
import * as reportingDataHttp from 'reporting-data/request/http';
import { useInterval } from '../../../../hooks/useInterval';
import { FAILED, PENDING } from '../ai-builder/constants/aiBuilder';
import { getIsReportChangedAndFetchNotDeferred, resultFormatter } from './useFetchReportAIAssistantInsights';
const INSIGHTS_FETCH_URL_V2 = 'reporting-insights/copilot/v2/generate';
const INSIGHTS_FETCH_STATUS_V2 = 'reporting-insights/copilot/v2/generate/status';
const DEBOUNCE_DELAY = 500;
const POLLING_INTERVAL = 2500;
const INITIAL_POLLING_DELAY = 5000;
/**
 * Hook that initiates the AI insights request and returns the task ID
 */
export const useInitiateFetchAsyncReportAIInsights = ({
  report,
  reportResolve,
  deferred = false,
  numberOfInsights = 3
}) => {
  const fetcher = useCallback(param => reportingDataHttp.post(INSIGHTS_FETCH_URL_V2, param, {
    ttl: DEBOUNCE_DELAY
  }), []);
  const requestArgs = useMemo(() => {
    const dataset = getReportDatasetFromReportResolve(report, reportResolve);
    return {
      data: {
        reportEgg: report.toJS(),
        dataset,
        numberOfInsights
      }
    };
  }, [report, reportResolve, numberOfInsights]);
  const asyncFetcher = useAsyncFetcher({
    requestArgs,
    fetcher,
    deferred
  });
  const taskId = useMemo(() => {
    if (isSucceeded(asyncFetcher)) {
      const status = asyncFetcher.data.get('status');
      if (status === PENDING) {
        return asyncFetcher.data.get('taskId');
      }
    }
    return null;
  }, [asyncFetcher]);
  return {
    taskId
  };
};

/**
 * Hook that polls for AI insights status using a task ID
 */
export const usePollReportAIInsightsStatus = ({
  taskId
}) => {
  const [isPolling, setIsPolling] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isFirstPoll, setIsFirstPoll] = useState(true);
  const prevTaskIdRef = useRef(null);
  useEffect(() => {
    if (taskId && prevTaskIdRef.current !== taskId) {
      prevTaskIdRef.current = taskId;
      setIsPolling(true);
      setIsFirstPoll(true);
      setResult(null);
      setError(null);
    } else {
      setIsPolling(false);
    }
  }, [taskId, result]);
  const currentPollingInterval = isFirstPoll ? INITIAL_POLLING_DELAY : POLLING_INTERVAL;
  const pollTaskStatus = useCallback(async () => {
    if (taskId) {
      try {
        const res = await http.get(`${INSIGHTS_FETCH_STATUS_V2}/${taskId}`);
        setIsFirstPoll(false);
        if (res.status === 'COMPLETED') {
          setIsPolling(false);
          const formattedResult = resultFormatter(fromJS(res.result));
          setResult(formattedResult);
        } else if (res.status === FAILED) {
          setIsPolling(false);
          setError(res.error);
        }
      } catch (err) {
        setIsFirstPoll(false);
        setIsPolling(false);
        setError(err);
        logError(err);
      }
    }
  }, [taskId]);
  useInterval(currentPollingInterval, pollTaskStatus, isPolling);
  return {
    isPolling,
    result,
    error
  };
};

/**
 *
 * @param {Report} report - The report object
 * @param {any} reportResolve - The report resolve object
 * @param {boolean} isDeferred - Whether the request is deferred
 * @param {number} numberOfInsights - The number of insights to fetch
 * @param {boolean} enableSameReportCaching - Whether to enable caching that returns the previous successful result if the report has not changed. If enabled, it will defer fetch of new insights and return cached result until a change has been made to report
 *
 * @returns {AsyncData<ReportingAIAssistantResponse>} - The async data containing the insights
 */
export const useFetchAsyncReportAIAssistantInsights = ({
  report,
  reportResolve,
  isDeferred = false,
  numberOfInsights = 3,
  enableSameReportCaching
}) => {
  const prevReportRef = useRef();
  const prevInsightFetchResultRef = useRef();
  const isReportChangedAndFetchNotDeferred = getIsReportChangedAndFetchNotDeferred(report, prevReportRef, isDeferred);
  const shouldDeferRequestBecauseReportIsSame = enableSameReportCaching && !isReportChangedAndFetchNotDeferred && !!prevInsightFetchResultRef.current;
  const shouldReturnCachedResult = enableSameReportCaching && !isReportChangedAndFetchNotDeferred && !!prevInsightFetchResultRef.current;
  const deferred = shouldDeferRequestBecauseReportIsSame || !reportResolve || !reportResolve.data || isDeferred;
  const {
    taskId
  } = useInitiateFetchAsyncReportAIInsights({
    report,
    reportResolve,
    deferred,
    numberOfInsights
  });
  useEffect(() => {
    if (isReportChangedAndFetchNotDeferred) {
      prevInsightFetchResultRef.current = undefined;
    }
  }, [isReportChangedAndFetchNotDeferred]);
  const {
    isPolling,
    result,
    error
  } = usePollReportAIInsightsStatus({
    taskId
  });
  useEffect(() => {
    if (result) {
      prevInsightFetchResultRef.current = result;
    }
  }, [result, prevInsightFetchResultRef]);
  let status = AsyncStatus.UNINITIALIZED;
  if (taskId && isPolling) {
    status = AsyncStatus.STARTED;
  } else if (result || shouldReturnCachedResult) {
    status = AsyncStatus.SUCCEEDED;
  } else if (error) {
    status = AsyncStatus.FAILED;
  }
  return {
    error,
    status,
    data: shouldReturnCachedResult ? prevInsightFetchResultRef.current : result
  };
};